import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { asistenteApi, asistenteKeys } from '../asistente.api';
import { selectConversacionId, useAsistenteStore } from '../asistente.store';

/**
 * Historial de conversaciones del asistente.
 *
 * El listado y el detalle vienen del servidor (cada usuario ve sólo lo suyo) y
 * se cachean con TanStack Query: al abrir el panel se ven al instante y se
 * refrescan cuando se borra o renombra algo.
 */

/** Listado de conversaciones, filtrable por texto. */
export function useConversaciones(buscar = '', habilitado = true) {
  return useQuery({
    queryKey: asistenteKeys.conversaciones(buscar),
    queryFn: ({ signal }) => asistenteApi.conversaciones(buscar, signal),
    staleTime: 30_000,
    enabled: habilitado,
    retry: false,
  });
}

/** Detalle de una conversación concreta (para reabrirla). */
export function useConversacion(id: number | null) {
  return useQuery({
    queryKey: asistenteKeys.conversacion(id ?? 0),
    queryFn: ({ signal }) => asistenteApi.conversacion(id as number, signal),
    enabled: Boolean(id),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** Renombra una conversación del historial. */
export function useRenombrarConversacion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, titulo }: { id: number; titulo: string }) =>
      asistenteApi.renombrarConversacion(id, titulo),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: asistenteKeys.all });
    },
  });
}

/** Borra una conversación; si era la abierta, la pantalla vuelve a empezar. */
export function useBorrarConversacion() {
  const queryClient = useQueryClient();
  const conversacionId = useAsistenteStore(selectConversacionId);
  const reset = useAsistenteStore((state) => state.reset);

  return useMutation({
    mutationFn: (id: number) => asistenteApi.borrarConversacion(id),
    onSuccess: (_resultado, id) => {
      if (id === conversacionId) reset();
      void queryClient.invalidateQueries({ queryKey: asistenteKeys.all });
    },
  });
}

/**
 * Recupera la conversación que el usuario tenía abierta.
 *
 * El id se persiste en el navegador, así que al recargar la página se vuelve a
 * leer del servidor (el contenido no se guarda en el cliente para no mostrar
 * cifras antiguas).
 *
 * @returns `true` mientras se está restaurando el hilo.
 */
export function useRestaurarConversacion(): boolean {
  const conversacionId = useAsistenteStore(selectConversacionId);
  const messages = useAsistenteStore((state) => state.messages);
  const abrirConversacion = useAsistenteStore((state) => state.abrirConversacion);
  const [restaurando, setRestaurando] = useState(false);

  const conversacion = useConversacion(messages.length === 0 ? conversacionId : null);

  useEffect(() => {
    if (!conversacion.data) return;

    abrirConversacion(conversacion.data);
    setRestaurando(false);
  }, [conversacion.data, abrirConversacion]);

  useEffect(() => {
    setRestaurando(Boolean(conversacionId) && messages.length === 0 && conversacion.isFetching);
  }, [conversacionId, messages.length, conversacion.isFetching]);

  return restaurando;
}
